import { z } from 'zod';
/**
 * Security settings — SSO + 2FA enforcement, session timeout, region
 * residency, and compliance posture. `promptRetentionDays: 0` is a
 * literal — LeanOpx never retains LLM prompts after the response is
 * delivered to the client; flipping this to a non-zero value is a
 * separate product decision that would change the schema literal.
 *
 * Compliance flags are advisory at the schema level; the hard
 * enforcement lives in the deployment region + audit retention windows.
 *
 * PR2 ships the schema standalone so `<SettingChoice>` and friends in
 * the Settings hub primitives can render typed enum dropdowns. The
 * `GET/PUT /api/orgs/:o/preferences/security` endpoints land with epic
 * #16 — they sit alongside the agent / cadence / billing tabs under
 * the unified `orgPreferences/{orgId}` doc.
 */
// Domain-prefixed names so the shared barrel doesn't collide with the
// existing `OrgRole` / `PlatformRole` exports from `org.ts`. Generic
// names like `Role` / `Region` are dangerous in a flat re-export.
export const SecurityRole = z.enum(['Admin', 'Editor', 'ReadOnly']);
export const SecurityRegion = z.enum([
    'us-east-1',
    'us-west-2',
    'eu-west-1',
    'ap-southeast-1',
]);
export const ComplianceStandard = z.enum([
    'SOC2-T2',
    'GDPR',
    'CCPA',
    'HIPAA-BAA',
]);
export const SecuritySettings = z.object({
    sso: z.object({
        /** Provider name, e.g. "okta", "google", "azure-ad". Empty when SSO is off. */
        provider: z.string(),
        enforced: z.boolean(),
    }),
    twoFA: z.object({
        requiredFor: z.array(SecurityRole),
    }),
    sessionTimeoutHours: z.number().positive().default(12),
    region: SecurityRegion,
    /** Optional override of the default storage bucket per region. */
    storageBucket: z.string().optional(),
    /**
     * Literal 0 — LeanOpx never retains LLM prompts post-response. Schema
     * change required to flip this; it's not a runtime setting.
     */
    promptRetentionDays: z.literal(0),
    compliance: z.array(ComplianceStandard),
});
